import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import FinancialPerformanceTable from "./FinancialPerformanceTable";
import { getFinancialPerformanceData } from "../../viewServices/apiCalls";
import {
  CardLoadingState,
  CardErrorState,
  CardEmptyState,
  CardNoDataState,
} from "../shared";

const MIN_HEIGHT = 180;

export default function CardFinancialPerformance({ programId, filters }) {
  const [pnlData, setPnlData] = useState(null);
  const [drillDownsOpen, setDrillDownsOpen] = useState([]);
  const [isLoading, setIsLoading] = useState(!!programId);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPnlData = async () => {
      if (!programId) {
        setIsLoading(false);
        setPnlData(null);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Fetch data by program - returns data_by_type (actual/forecast/policy)
        const response = await getFinancialPerformanceData(programId, filters);
        const dataByType = response.data_by_type || {};

        // Extract P&L data for each scenario type
        const actualData = dataByType.actual;
        const forecastData = dataByType.forecast;
        const policyData = dataByType.policy;

        // Use first available data for structure
        const baseData = actualData || forecastData || policyData;

        if (!baseData) {
          setPnlData(null);
          return;
        }

        // Build combined P&L data structure
        setPnlData({
          plStructure: baseData.plStructure,
          netRevenueAnchor: baseData.netRevenueAnchor,
          serveMarginAnchor: baseData.serveMarginAnchor,
          volumeUnit: baseData.volumeUnit,
          volumeL12M: baseData.volumeL12M,
          currencyUnit: baseData.currencyUnit,
          showValueType: baseData.showValueType || "value",
          plValues: {
            1: actualData?.plValues?.[1] || {},
            2: forecastData?.plValues?.[1] || {},
            3: policyData?.plValues?.[1] || {},
          },
          drillDownsOpen: baseData.drillDownsOpen || [],
        });
      } catch (err) {
        console.error("Error fetching P&L data:", err);
        setError(err.message || "Failed to load P&L data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPnlData();
  }, [programId, filters]);

  // Loading state
  if (isLoading) {
    return <CardLoadingState minHeight={MIN_HEIGHT} />;
  }

  // Error state
  if (error) {
    return <CardErrorState message={error} minHeight={MIN_HEIGHT} />;
  }

  // Empty state (no program selected)
  if (!programId) {
    return <CardEmptyState minHeight={MIN_HEIGHT} />;
  }

  // No data state
  if (!pnlData) {
    return (
      <CardNoDataState
        title="Nenhum dado de P&L disponível"
        subtitle="Verifique os scenarios do program"
        minHeight={MIN_HEIGHT}
      />
    );
  }

  return (
    <FinancialPerformanceTable
      plStructure={pnlData.plStructure.current}
      netRevenueAnchor={pnlData.netRevenueAnchor}
      serveMarginAnchor={pnlData.serveMarginAnchor}
      plName="P&L Overview"
      drillDownsOpen={drillDownsOpen}
      setDrillDownsOpen={setDrillDownsOpen}
      plValues={pnlData.plValues}
      volumeUnit={pnlData.volumeUnit}
      volumeL12M={pnlData.volumeL12M}
      currencyUnit={pnlData.currencyUnit}
      showValueType={pnlData.showValueType}
      nameOnly={false}
      showColumns={["name", "val1", "val2", "val3"]}
      nameAlignRight={false}
    />
  );
}

CardFinancialPerformance.propTypes = {
  programId: PropTypes.number,
  filters: PropTypes.object,
};
